/* @flow */
import * as React from "react";
import styled, { ThemeConsumer } from "styled-components";
import connected from "../common/connected";
import { getViewedStock } from "../../redux/reducer/tabs";
import Gettext from "../common/Gettext";
import IntervalsModal from "../TradeModals/IntervalsModal";
import ActiveStock from "./ActiveStock";
import { VerticalTicks, HorizontalTicks } from "./NextGenTicks";
import Surface from "./Surface";
import Line from "./Line";
import Candlestick from "./Candlestick";
import Bar from "./Bar";
import Indicator from "./Indicator";
import OscillatorVisuals, {
  getRelativeHeight,
  getOscillatorHeightInPercents
} from "./OscillatorVisuals";
import Deal, { dealDuplicatesReducer } from "./Deal";
import Dot from "./Dot";
import background from "./background.png";
import { HorizontalBar, VerticalBar } from "./Bars";
import Sentiments from "./Sentiments";
import ChartBottomNavigation from "./ChartBottomNavigation";
import * as Types from "./NextGenChartTypes";
import Layers from "./Layers";
import StatelessToolbar from "../TradeMobile/Toolbar/Toolbar";
import { TourContext } from "../Tour";
import OnLine, { OfflineBadge } from "../OnLine";

const SurfaceContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const Toolbar = connected(StatelessToolbar)((state: State) => {
  const activeStock = getViewedStock(state.tabs);

  return {
    activeStock,
    settings: state.settings,
    isOTC:
      state.stocks != null && state.stocks[activeStock] != null
        ? state.stocks[activeStock].otc
        : false
  };
});

function Underlay(props: Types.VisualProps) {
  return (
    <React.Fragment>
      <ActiveStock mobile={true}>{props.stock.stock}</ActiveStock>
      <HorizontalTicks
        scaleX={props.scaleX}
        ticks={props.scaleX.ticks(3)}
        mobile
      />
      <VerticalTicks
        scaleY={props.scaleY}
        ticks={props.scaleY.ticks(3)}
        mobile
      />
      <OnLine>{status => !status && <OfflineBadge />}</OnLine>
    </React.Fragment>
  );
}

function fitsViewport(props: Types.VisualProps, vector) {
  const [x, y] = vector;
  return x >= 0 && x <= props.width && y >= 0 && y <= props.height;
}

function Overlay(props: Types.VisualProps) {
  const {
    scaleX,
    scaleY,
    settings,
    lastPoint,
    tradeMode,
    lineColor,
    chartHeight
  } = props;

  const [lastPointX, lastPointY, lastPointSource] = lastPoint || [];

  const estimatedDealEndingX = scaleX(
    lastPointSource.date + settings.currentInterval * 1000
  );

  return (
    <React.Fragment>
      {props.deals
        .reduce(dealDuplicatesReducer, [])
        .map((deal, index, deals) => (
          <Deal
            key={deal.id != null ? deal.id : index.toString(10)}
            {...deal}
            now={props.time}
            scaleX={scaleX}
            scaleY={scaleY}
            viewportHeight={props.height}
            viewportWidth={props.width}
            lastPoint={lastPoint}
            opacity={Math.floor((1 / (deals.length + 1)) * 100) / 100}
            tradeMode={tradeMode}
            refundClickHandler={props.refundClickHandler}
            refundFetching={props.refundFetching}
            chartHeight={chartHeight}
            mobile
          />
        ))}

      {lastPoint && (
        <React.Fragment>
          {fitsViewport(props, [lastPointX, 0]) && (
            <VerticalBar mobile={true} opening={true} x={lastPointX}>
              <Gettext>Opening of the trade</Gettext>
            </VerticalBar>
          )}
          {estimatedDealEndingX - lastPointX > 5 &&
            fitsViewport(props, [estimatedDealEndingX, 0]) && (
              <VerticalBar
                mobile={true}
                switchSide={true}
                x={estimatedDealEndingX}
              >
                <Gettext>Closing of the trade</Gettext>
              </VerticalBar>
            )}

          <HorizontalBar
            y={lastPointY}
            value={lastPointSource.value}
            viewportHeight={props.height}
            mobile
          />

          {props.settings.chartType === "line" && (
            <Dot x={lastPointX} y={lastPointY} color={lineColor} mobile />
          )}
        </React.Fragment>
      )}
      {props.settings.sentiments && <Sentiments stats={props.stats} mobile />}
    </React.Fragment>
  );
}

class Visuals extends React.Component<Types.VisualProps> {
  static contextType = TourContext;
  state = {
    isShownCandleModal: false
  };

  toggleCandleModal = () => {
    this.setState(state => ({
      isShownCandleModal: !state.isShownCandleModal
    }));
  };
  render() {
    const {
      points,
      width,
      height,
      scaleX,
      scaleY,
      settings,
      theme
    } = this.props;

    let getOscillatorBlockHeight = () => 0;
    let getOscillatorHeight = () => 0;

    if (this.props.oscillators.length) {
      const oscillatorHeightInPercents = getOscillatorHeightInPercents(
        this.props.oscillators.length
      );
      getOscillatorBlockHeight = height =>
        getRelativeHeight(height, oscillatorHeightInPercents);
      getOscillatorHeight = height =>
        Math.floor(height / this.props.oscillators.length);
    }

    const oscillatorsHeight = getOscillatorBlockHeight(height);
    const chartHeight = height - oscillatorsHeight;
    const oscillatorHeight = getOscillatorHeight(oscillatorsHeight);

    let lineColor =
      settings.lineColor === theme.background
        ? theme.foreground
        : settings.lineColor;

    const toolbarForTour =
      this.context != null && this.context.step === "showStockSelect";

    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          position: "absolute",
          top: 0,
          left: 0,
          display: "flex",
          flexDirection: "column"
        }}
        ref={this.props.outerCountainerRef}
      >
        <div
          style={{
            flex: "1 1 auto",
            position: "relative",
            height: "100%",
            top: 0,
            left: 0,
            backgroundImage: `url(${background})`
          }}
          {...this.props.containerProps}
        >
          <Underlay {...this.props} />

          {this.props.settings.chartType === "candlestick" && (
            <Candlestick
              candles={this.props.candles}
              viewportWidth={this.props.width}
            />
          )}

          {this.props.settings.chartType === "bar" && (
            <Bar
              candles={this.props.candles}
              viewportWidth={this.props.width}
            />
          )}

          <SurfaceContainer id="surface-container">
            <div
              style={{
                // height: `${chartHeightInPercents}%`,
                height: `${chartHeight}px`
              }}
            >
              <Surface width={width} height={height}>
                {this.props.settings.chartType === "line" && (
                  <Line
                    data={points}
                    width={width}
                    height={height}
                    scaleX={scaleX}
                    scaleY={scaleY}
                    color={lineColor}
                    strokeWidth={settings.lineThickness}
                    displayGradient={false}
                  />
                )}

                {settings.indicators
                  .filter(indicator => indicator.enabled !== false)
                  .map((config, index) => (
                    <Indicator
                      key={index}
                      data={points}
                      width={width}
                      height={height}
                      scaleX={scaleX}
                      scaleY={scaleY}
                      config={config}
                      interval={this.props.interval}
                    />
                  ))}
              </Surface>
            </div>

            <OscillatorVisuals
              stock={this.props.stock.stock}
              width={width}
              height={`${oscillatorsHeight}px`}
              oscillatorHeight={oscillatorHeight}
              indicators={this.props.settings.indicators}
              points={points}
              rawData={this.props.rawData}
              scaleX={scaleX}
              scaleY={scaleY}
              interval={this.props.interval}
              onHide={index => {
                this.props.dispatch({
                  type: "TOGGLE_INDICATOR",
                  payload: index
                });
              }}
              onRemove={index =>
                this.props.dispatch({
                  type: "REMOVE_INDICATOR",
                  payload: index
                })
              }
              mobile
            />
          </SurfaceContainer>

          <Overlay
            {...this.props}
            lineColor={lineColor}
            chartHeight={chartHeight}
          />
        </div>

        {this.state.isShownCandleModal && (
          <IntervalsModal
            value={this.props.interval}
            onChange={this.props.onIntervalChange}
            onToggle={this.toggleCandleModal}
            stock={this.props.stock}
          />
        )}

        <div
          style={{
            position: "absolute",
            top: 0,
            width: "100%",
            transition: toolbarForTour ? "none" : "all 0.5s ease",
            opacity: this.props.isEngaged || toolbarForTour ? 1 : 0,
            transform:
              this.props.isEngaged || toolbarForTour
                ? "translateY(30px)"
                : "translateY(-40px)",
            zIndex: Layers.SENTIMENTS + 2
          }}
        >
          <Toolbar />
        </div>

        <div
          style={{
            position: "absolute",
            bottom: 0,
            width: "100%",
            transition: "all 0.5s ease",
            opacity: this.props.isEngaged ? 1 : 0,
            transform: this.props.isEngaged
              ? "translateY(0)"
              : "translateY(70px)",
            zIndex: Layers.SENTIMENTS + 1
          }}
        >
          <ChartBottomNavigation
            loading={this.props.loading}
            timeSpan={this.props.timeSpan}
            onTimeSpanChange={this.props.onTimeSpanChange}
            onBackToReality={this.props.onBackToReality}
            interval={this.props.interval}
            onIntervalChange={this.props.onIntervalChange}
            chartType={this.props.settings.chartType}
            isShownCandleModal={this.state.isShownCandleModal}
            toggleCandleModal={this.toggleCandleModal}
            stock={this.props.stock}
            now={this.props.lastPoint[2].date}
          />
        </div>
      </div>
    );
  }
}
export default (props: Types.VisualProps) => (
  <ThemeConsumer>{theme => <Visuals {...props} theme={theme} />}</ThemeConsumer>
);



// WEBPACK FOOTER //
// ./src/components/Chart/NextGenMobileVisuals.js